/** Shared by the website loader and settings preview; no framework dependencies. */
export const LAUNCHER_ICONS = [
    { id: 'default', label: 'Original' },
    { id: 'bubble', label: 'Bubble' },
    { id: 'dots', label: 'Dots' },
    { id: 'smile', label: 'Smile' },
    { id: 'outline', label: 'Outline' },
    { id: 'grin', label: 'Happy' },
] as const;

export interface LauncherSettings {
    style: 'circle' | 'pill';
    icon: (typeof LAUNCHER_ICONS)[number]['id'];
    text: string;
}

export function resolveLauncher(settings?: Partial<LauncherSettings>): LauncherSettings {
    return {
        style: settings?.style ?? 'circle',
        icon: settings?.icon ?? 'default',
        text: settings?.text ?? 'Chat with us',
    };
}

const artwork = {
    default:
        '<svg width="28" height="28" viewBox="0 0 98 114.55" aria-hidden="true"><path d="M15.95 0h66.09c8.81 0 15.95 7.14 15.95 15.95v94.59c0 3.22-3.61 5.12-6.26 3.3L67.11 96.97H15.95C7.14 96.97 0 89.83 0 81.02V15.95C0 7.14 7.14 0 15.95 0" fill="currentColor"/></svg>',
    bubble: '<path d="M12 3C6.5 3 2 6.7 2 11.4c0 2.5 1.3 4.8 3.5 6.3L4.5 22l5-2.4c.8.2 1.6.3 2.5.3 5.5 0 10-3.8 10-8.5S17.5 3 12 3Z" fill="currentColor"/>',
    dots: '<path fill="currentColor" fill-rule="evenodd" d="M6 3a4 4 0 0 0-4 4v9a4 4 0 0 0 4 4h2v3l5-3h5a4 4 0 0 0 4-4V7a4 4 0 0 0-4-4H6Zm1 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/>',
    smile: '<path fill="currentColor" fill-rule="evenodd" d="M5 2a3 3 0 0 0-3 3v13a3 3 0 0 0 3 3h11l6 3V5a3 3 0 0 0-3-3H5Zm1.3 11.3a1 1 0 0 1 1.4 0 6.1 6.1 0 0 0 8.6 0 1 1 0 0 1 1.4 1.4 8.1 8.1 0 0 1-11.4 0 1 1 0 0 1 0-1.4Z"/>',
    grin: '<path fill="currentColor" fill-rule="evenodd" d="M12 1.5C6 1.5 1.2 6.1 1.2 11.8c0 3 1.3 5.7 3.5 7.6V23l3.8-2.1c1.1.3 2.3.5 3.5.5 6 0 10.8-4.6 10.8-10.3S18 1.5 12 1.5ZM7.35 11.6a1.1 1.1 0 0 1 1.55.05c1.75 1.9 4.45 1.9 6.2 0a1.1 1.1 0 1 1 1.6 1.5c-2.6 2.8-6.8 2.8-9.4 0a1.1 1.1 0 0 1 .05-1.55Z"/>',
    outline:
        '<path d="M21 11.5a9 9 0 0 1-9 9 10 10 0 0 1-4-.9L3 21l1.4-5a9 9 0 1 1 16.6-4.5Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M8 9h8M8 13h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
};

export function launcherIcon(icon: LauncherSettings['icon']) {
    return icon === 'default' || !artwork[icon]
        ? artwork.default
        : `<svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">${artwork[icon]}</svg>`;
}

export const LAUNCHER_STYLES = `
.launcher { box-sizing: border-box; position: relative; flex: none; width: 60px; height: 60px; padding: 0; border-radius: 50%; border: 0; cursor: pointer;
  background: var(--brand); color: #fff; display: grid; place-items: center; font: 600 16px/1.3 Inter, ui-sans-serif, system-ui, sans-serif;
  box-shadow: 0 2px 32px rgba(0,0,0,.16), 0 1px 6px rgba(0,0,0,.06); transition: transform .2s cubic-bezier(.2,.8,.2,1); }
.launcher[hidden] { display: none; }
.launcher:hover { transform: scale(1.07); }
.launcher:active { transform: scale(.96); }
.launcher:focus-visible { outline: 3px solid var(--brand); outline-offset: 4px; }
.launcher .icon { display: flex; align-items: center; justify-content: center; gap: 10px; transition: transform .25s, opacity .2s; }
.launcher .icon-close { position: absolute; opacity: 0; transform: rotate(-90deg) scale(.6); }
.launcher .launcher-graphic { position: relative; display: grid; place-items: center; width: 28px; height: 28px; flex: none; }
.launcher.pill { height: 48px; width: max-content; max-width: min(320px, calc(100vw - 40px)); border-radius: 24px; padding: 0 18px; }
.launcher.pill .icon-chat { gap: 8px; min-width: 0; max-width: 100%; }
.launcher .launcher-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.open .launcher { width: 60px; padding: 0; }
.open .launcher.pill { width: 48px; padding: 0; }
.launcher.pill .launcher-graphic, .launcher.pill .launcher-graphic svg { width: 24px; height: 24px; }
.open .launcher .icon-chat { opacity: 0; transform: scale(.6); pointer-events: none; }
.open .launcher .icon-close { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) { .launcher, .launcher .icon { transition: none; } }
`;

/** Use DOM text/attribute setters for user content, never interpolate it as HTML. */
export function populateLauncher(button: HTMLButtonElement, input?: Partial<LauncherSettings>) {
    const settings = resolveLauncher(input);
    button.classList.toggle('pill', settings.style === 'pill');
    button.querySelector('.icon-chat')?.remove();
    button.querySelector('.icon-close')?.remove();
    const content = document.createElement('span');
    content.className = 'icon icon-chat';
    const graphic = document.createElement('span');
    graphic.className = 'launcher-graphic';
    graphic.innerHTML = launcherIcon(settings.icon);
    content.appendChild(graphic);
    if (settings.style === 'pill') {
        const text = document.createElement('span');
        text.className = 'launcher-text';
        text.textContent = settings.text.trim() || 'Chat with us';
        content.appendChild(text);
    }
    const close = document.createElement('span');
    close.className = 'icon icon-close';
    close.innerHTML =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
    button.prepend(content, close);
}
